/**
 * DOMPurify, fetched when nothing else is happening rather than at startup.
 *
 * It is 34 kB of the initial bundle for a job that only ever runs when somebody prints, so it
 * arrives as its own chunk. The catch is the tap itself: on iOS, `print()` is only allowed while
 * the user's gesture is still live, and awaiting a network round trip in between loses it. So the
 * module is fetched ahead of time — at the first idle moment, and again when the documents tab
 * opens, whichever comes first — and the tap finds it already in hand: {@link sanitizePrintHtml}
 * is synchronous, and never fetches anything.
 */

/** What this file uses of DOMPurify, and nothing more. */
interface HtmlPurifier {
  sanitize(
    html: string,
    config: Readonly<{
      WHOLE_DOCUMENT: boolean;
      ADD_TAGS: readonly string[];
      ADD_ATTR: readonly string[];
    }>,
  ): string;
}

const DOCTYPE = '<!doctype html>';

let purifier: HtmlPurifier | null = null;
let arriving: Promise<HtmlPurifier> | null = null;
let loads = 0;

export class PrintSanitizerNotReadyError extends Error {
  constructor() {
    super('The print sanitizer has not been loaded yet');
    this.name = 'PrintSanitizerNotReadyError';
  }
}

/**
 * Starts the fetch, or hands back the one already in flight. Calling it again after it has
 * resolved costs nothing: the module is kept.
 */
export const preloadPrintSanitizer = async (): Promise<void> => {
  if (purifier !== null) return;
  arriving ??= (async () => {
    loads += 1;
    const module = await import('dompurify');
    return module.default as unknown as HtmlPurifier;
  })();
  purifier = await arriving;
};

/** Whether a tap can go straight to the printer without waiting for anything. */
export const isPrintSanitizerReady = (): boolean => purifier !== null;

/** How many times the module was actually fetched. Read by the test that guards the tap. */
export const printSanitizerLoadCount = (): number => loads;

/**
 * DOMPurify drops the doctype and every `<meta>` by default. Without them the printed document
 * renders in quirks mode and falls back to the host encoding — mojibake risk on the Arabic
 * templates. `charset` is re-allowed on `<meta>`; `http-equiv` and `content` stay forbidden, so
 * a `meta refresh` injected through content cannot survive.
 */
export const sanitizePrintHtml = (html: string): string => {
  if (purifier === null) throw new PrintSanitizerNotReadyError();
  return `${DOCTYPE}${String(
    purifier.sanitize(html, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ['meta'],
      ADD_ATTR: ['charset'],
    }),
  )}`;
};

/** Test seam: forget the module, so a spec can watch it being fetched again. */
export const forgetPrintSanitizer = (): void => {
  purifier = null;
  arriving = null;
  loads = 0;
};
